import { MutableRefObject, useCallback, useEffect, useRef } from 'react';
import { UseClickOutsideProps } from './props';

/**
 * Custom hook to detect clicks outside a specified element.
 * This hook is useful for closing dropdowns, modals, or any component when a user clicks outside of it.
 *
 * @param {UseClickOutsideProps} props - The properties for the hook.
 * @param {Function} props.onClickOutside - Callback function to be called when a click outside is detected.
 * @param {boolean} props.open - Boolean indicating if the component is open.
 * @param {boolean} [props.disabled=false] - Boolean to disable the click outside detection.
 * @param {MutableRefObject<HTMLElement>} [props.customRef] - Optional custom ref to check for outside clicks.
 *
 * @returns {MutableRefObject<HTMLElement>} - A ref to be attached to the element to monitor for outside clicks.
 *
 * Example usage:
 * const ref = useClickOutside({
 *   onClickOutside: () => function('Clicked outside!'),
 *   open: true,
 *   disabled: false,
 *   customRef: someCustomRef,
 * });
 * <div ref={ref}>Content</div>
 */
export function useClickOutside({
  onClickOutside,
  open,
  disabled = false,
  customRef,
}: UseClickOutsideProps): MutableRefObject<HTMLElement> {
  // Create a ref to attach to the element we want to monitor for outside clicks
  const ref = useRef<HTMLElement>(null);

  // Callback function to handle click events
  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      // Check if the click is on a specific button element
      const buttonEl = document?.getElementById('input-select-button');

      // If the click is on the button, do nothing
      if (buttonEl && buttonEl.contains(event.target as Node)) {
        return;
      }

      // Check if the click is outside the monitored element and the custom ref, if provided
      if (
        ((ref.current && !ref.current.contains(event.target as Node)) ||
          (customRef?.current &&
            !customRef?.current.contains(event.target as Node))) &&
        open &&
        !disabled
      ) {
        // Call the onClickOutside callback if conditions are met
        onClickOutside();
      }
    },
    [ref, customRef, open, onClickOutside, disabled],
  );

  // Effect to add and clean up the event listener for mouseup events
  useEffect(() => {
    document.addEventListener('mouseup', handleClickOutside);
    return () => {
      document.removeEventListener('mouseup', handleClickOutside);
    };
  }, [handleClickOutside]);

  // Return the ref to be used by the component
  return ref;
}

export type { UseClickOutsideProps };
